import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useOrgStore } from './orgStore.js'
import { fetchTicketHistoryRequest, runTicketOperationRequest } from '../services/ticketService.js'
import { parseTicketOperationError } from '../utils/ticketUi.js'

export const useTicketOpsStore = defineStore('ticketOps', () => {
  const orgStore = useOrgStore()
  const ticketHistory = ref([])
  const loading = ref(false)
  const operationLoading = ref(false)
  const activeOperationKey = ref('')
  const error = ref('')
  const summary = ref(null)

  const toOperationKey = (lotteryId, action, ticketNumbers) => {
    return `${String(lotteryId)}:${String(action)}:${ticketNumbers.join(',')}`
  }

  const normalizeTickets = (ticketNumbers) => {
    return [...new Set((Array.isArray(ticketNumbers) ? ticketNumbers : [])
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0))]
      .sort((a, b) => a - b)
  }

  const fetchTicketHistory = async (lotteryId) => {
    loading.value = true
    error.value = ''

    try {
      const data = await fetchTicketHistoryRequest(lotteryId, orgStore.activeOrgId)
      ticketHistory.value = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : [])
      return { success: true, data: ticketHistory.value }
    } catch (err) {
      ticketHistory.value = []
      error.value = parseTicketOperationError(err, 'Could not load ticket history.')
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const performOperation = async (lotteryId, action, ticketNumbers) => {
    const normalizedTickets = normalizeTickets(ticketNumbers)
    if (!normalizedTickets.length) {
      return { success: false, error: 'Select at least one ticket to continue.' }
    }

    const operationKey = toOperationKey(lotteryId, action, normalizedTickets)
    if (operationLoading.value && activeOperationKey.value === operationKey) {
      return { success: false, code: 'OPERATION_IN_PROGRESS', error: 'The same ticket operation is already in progress.' }
    }

    operationLoading.value = true
    activeOperationKey.value = operationKey
    error.value = ''
    summary.value = null

    try {
      const payload = {
        action,
        ticket_numbers: normalizedTickets,
      }
      const data = await runTicketOperationRequest(lotteryId, payload, orgStore.activeOrgId)
      const succeeded = Array.isArray(data?.succeeded) ? data.succeeded : normalizedTickets
      const failed = Array.isArray(data?.failed) ? data.failed : []

      if (Array.isArray(data?.history)) {
        ticketHistory.value = data.history
      }

      summary.value = {
        action,
        requested: normalizedTickets.length,
        succeeded: succeeded.length,
        failed: failed.length,
        failures: failed,
      }

      if (failed.length) {
        return {
          success: false,
          partial: true,
          error: `Operation completed with partial failures (${failed.length}).`,
          summary: summary.value,
        }
      }

      return { success: true, summary: summary.value }
    } catch (err) {
      const mappedError = parseTicketOperationError(err, 'Ticket operation failed.')
      error.value = mappedError
      return { success: false, error: mappedError }
    } finally {
      operationLoading.value = false
      activeOperationKey.value = ''
    }
  }

  return {
    ticketHistory,
    loading,
    operationLoading,
    activeOperationKey,
    error,
    summary,
    fetchTicketHistory,
    performOperation,
  }
})

